import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CornerDownLeft, Loader2, Sparkles, Undo2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui";
import { cn } from "@/lib/utils";

interface QuickResult {
  kind: string;
  summary: string;
  link?: string;
  undo?: { type: string; id: string };
}

const EXAMPLES = [
  "call Jay Thursday",
  "Adrian: fix the checkout tomorrow at 3pm",
  "post on Instagram every Mon, Wed and Fri",
  "partner Sarah Lee @sarahgrows",
  "AI: research 5 podcasts to pitch next week",
  "VSL live by Nov 15",
];

/** "Type, don't fill": one line becomes a task, a routine, an affiliate or a goal. */
export function QuickAdd({ onAdded, className, autoFocus }: { onAdded?: () => void; className?: string; autoFocus?: boolean }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<QuickResult | null>(null);
  const [placeholder] = useState(() => EXAMPLES[Math.floor(Math.random() * EXAMPLES.length)]);
  const input = useRef<HTMLInputElement>(null);

  const submit = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const r = await api.post<QuickResult>("/command/quick", { text: t });
      setResult(r);
      setText("");
      onAdded?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add that");
    } finally {
      setBusy(false);
      input.current?.focus();
    }
  };

  const undo = async () => {
    if (!result?.undo) return;
    try {
      await api.post("/command/quick/undo", result.undo);
      setResult(null);
      onAdded?.();
      toast.success("Taken back");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't undo");
    }
  };

  return (
    <div className={className}>
      <form
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
        className="group flex items-center gap-2 rounded-2xl border border-zinc-800 bg-zinc-900/70 px-3 py-2 transition-colors focus-within:border-indigo-400/50 focus-within:bg-zinc-900"
      >
        <Sparkles className="h-4 w-4 shrink-0 text-indigo-300" />
        <input
          ref={input}
          autoFocus={autoFocus}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Type anything… e.g. "${placeholder}"`}
          className="min-w-0 flex-1 bg-transparent py-1 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
          aria-label="Add a task, routine, affiliate or goal"
        />
        <button
          type="submit"
          disabled={!text.trim() || busy}
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded-xl px-3 text-xs font-semibold transition-colors",
            text.trim() ? "cc-brand-bg text-white" : "bg-zinc-800 text-zinc-500",
          )}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CornerDownLeft className="h-3.5 w-3.5" />}
          Add
        </button>
      </form>
      {result && (
        <div className="cc-rise mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
          <span className="min-w-0 flex-1">{result.summary}</span>
          {result.link && <Link to={result.link} className="font-semibold text-emerald-100 underline-offset-2 hover:underline">Open</Link>}
          {result.undo && (
            <button type="button" onClick={undo} className="inline-flex items-center gap-1 font-semibold text-emerald-100 hover:underline">
              <Undo2 className="h-3 w-3" /> Undo
            </button>
          )}
        </div>
      )}
    </div>
  );
}
